import pb, { requireAuth } from '../pocketbaseClient';

class FileUploadService {
    /**
     * Upload a file for a submission (public — applicants are not authenticated).
     * Creates a record in the `submission_files` collection.
     */
    async uploadSubmissionFile(file, metadata = {}) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('original_name', file.name);
            formData.append('file_type', file.type);
            formData.append('file_size', String(file.size));

            if (metadata.questionId) {
                formData.append('question_id', String(metadata.questionId));
            }
            if (metadata.submissionId) {
                formData.append('submission_id', String(metadata.submissionId));
            }

            const record = await pb.collection('submission_files').create(formData);

            return {
                id: record.id,
                url: this.getFileUrl(record),
                originalName: record.original_name || file.name,
                fileType: record.file_type || file.type,
                fileSize: record.file_size || file.size
            };
        } catch (error) {
            // Log the full PocketBase error details for debugging
            if (error?.data) {
                console.error('PocketBase error details:', JSON.stringify(error.data, null, 2));
            }
            console.error('Error uploading submission file:', error);
            throw new Error(this.describeUploadError(error, file));
        }
    }

    /**
     * Translate a PocketBase upload error into a user-facing German message.
     */
    describeUploadError(error, file) {
        const label = file?.name ? `Die Datei "${file.name}"` : 'Die Datei';
        // nginx body-size limit (client_max_body_size) surfaces as HTTP 413.
        if (error?.status === 413) {
            return `${label} ist zu groß für den Server. Bitte wählen Sie eine kleinere Datei.`;
        }
        const fieldError = error?.data?.data?.file;
        if (fieldError?.code === 'validation_file_size_limit') {
            const maxBytes = fieldError?.params?.maxSize;
            const maxLabel = maxBytes ? this.formatBytes(maxBytes) : null;
            return maxLabel
                ? `${label} ist zu groß. Maximal erlaubt sind ${maxLabel}.`
                : `${label} ist zu groß.`;
        }
        if (fieldError?.code === 'validation_invalid_mime_type' || fieldError?.code === 'validation_invalid_file_type') {
            return `${label} hat einen nicht erlaubten Dateityp.`;
        }
        if (fieldError?.message) {
            return `${label} konnte nicht hochgeladen werden: ${fieldError.message}`;
        }
        return `${label} konnte nicht hochgeladen werden.`;
    }

    formatBytes(bytes) {
        if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
        return `${bytes} Bytes`;
    }

    /**
     * Fetch file records by their IDs (admin only).
     * Files are linked to a submission via the `fileIds` stored in the
     * submission's answers, so no `submission_id` back-reference is needed.
     */
    async getFilesByIds(fileIds) {
        try {
            requireAuth();
            if (!Array.isArray(fileIds) || fileIds.length === 0) return [];
            const filter = fileIds.map(id => `id="${id}"`).join(' || ');
            const records = await pb.collection('submission_files').getFullList({
                filter,
                sort: '-created'
            });

            // The `file` field is marked "Protected" in PocketBase, so download
            // URLs need a short-lived file token (expires after ~2 minutes).
            const fileToken = await pb.files.getToken();

            return records.map(record => ({
                id: record.id,
                url: this.getFileUrl(record, fileToken),
                originalName: record.original_name || '',
                fileType: record.file_type || '',
                fileSize: record.file_size || 0,
                questionId: record.question_id || '',
                createdAt: record.created
            }));
        } catch (error) {
            console.error('Error fetching files by ids:', error);
            throw new Error(`Failed to fetch files: ${error.message}`);
        }
    }

    /**
     * Build a download URL for a file record. Pass a file token (from
     * pb.files.getToken()) to access protected files.
     */
    getFileUrl(fileRecord, fileToken = '') {
        if (!fileRecord || !fileRecord.file) return '';
        const url = `${pb.baseUrl}/api/files/${fileRecord.collectionId}/${fileRecord.id}/${fileRecord.file}`;
        return fileToken ? `${url}?token=${encodeURIComponent(fileToken)}` : url;
    }

    /**
     * Delete a submission file (admin only).
     */
    async deleteSubmissionFile(fileId) {
        try {
            requireAuth();
            await pb.collection('submission_files').delete(fileId);
            return true;
        } catch (error) {
            console.error('Error deleting submission file:', error);
            throw new Error(`Failed to delete file: ${error.message}`);
        }
    }

}

const fileUploadService = new FileUploadService();
export default fileUploadService;
